<script setup>
import { ref, computed, onMounted } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

const props = defineProps(['totalTime', 'timeLeft']);
const emit = defineEmits(['timeEnd']);
const { t } = useI18n({ useScope: 'global' });

const timeStringToSeconds = (timeString) => {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
};

const formatSeconds = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
};

const totalTime = timeStringToSeconds(props.totalTime);
const timeLeft = ref(timeStringToSeconds(props.timeLeft));

const percentage = computed(() => (timeLeft.value / totalTime) * 100);
const formattedTime = computed(() => formatSeconds(timeLeft.value));

const { pause, resume } = useIntervalFn(() => {
    if (timeLeft.value > 0) {
        timeLeft.value--;
    } else {
        pause();
        emit('timeEnd');
    }
}, 1000);

onMounted(() => {
    resume();
});
</script>

<template>
    <div class="timer-bar-container">
        <div class="timer-content">
            <span class="timer-label">{{ t('tests.timeLimit') }}</span>
            <span class="timer-time">{{ formattedTime }}</span>
        </div>
        <div class="timer-progress-bg">
            <div
                class="timer-progress-fill"
                :style="{ width: percentage + '%' }"
            ></div>
        </div>
    </div>
</template>

<style scoped>
.timer-bar-container {
    @apply fixed top-0 left-0 right-0 z-50 bg-white shadow-md;
}

.timer-content {
    @apply flex items-center justify-between px-6 py-3;
}

.timer-label {
    @apply text-sm font-semibold text-gray-700;
}

.timer-time {
    @apply text-lg font-bold text-gray-900;
}

.timer-progress-bg {
    @apply w-full h-1 bg-gray-200;
}

.timer-progress-fill {
    @apply h-full bg-blue-500 transition-all duration-1000 ease-linear;
}

/* Mobile adaptations */
@media (max-width: 768px) {
    .timer-content {
        @apply px-4 py-2;
    }

    .timer-label {
        @apply text-xs;
    }

    .timer-time {
        @apply text-base;
    }
}

@media (max-width: 480px) {
    .timer-content {
        @apply px-3 py-2;
    }

    .timer-label {
        @apply text-xs;
    }

    .timer-time {
        @apply text-sm;
    }
}
</style>
